/**
 * The bot plays like a player: it sees the opponent with a reaction delay and outputs the same
 * inputs a human would (docs/fight.md §8). Deterministic given its seed.
 */
import { BOT, type BotDifficulty } from '../config/bot';
import { FIGHT } from '../config/fight';
import { reachOf, TICK_SECONDS } from '../sim/fight';
import { BUTTON, type FighterInput } from '../sim/input';
import { length } from '../sim/math';
import { moveCost } from '../sim/mods';
import { nextRandom, randomRange, type RngHolder } from '../sim/rng';
import type { ActionType, FightState, Side } from '../sim/types';

export type PlanStyle = 'pressure' | 'counter' | 'grinder';
export type RoundPlan = 'feelOut' | 'push' | 'finish';
export type WhenHurt = 'cover' | 'swingBack';

export interface GamePlan {
  style: PlanStyle;
  /** 0–100; 50 plays like the base bot. */
  aggression: number;
  rounds: readonly RoundPlan[];
  whenHurt: WhenHurt;
}

export const DEFAULT_PLAN: GamePlan = {
  style: 'pressure',
  aggression: 50,
  rounds: ['push', 'push', 'push'],
  whenHurt: 'cover',
};

interface Observation {
  x: number;
  y: number;
  actType: ActionType | null;
  actSeq: number;
  blocking: boolean;
  rocked: boolean;
}

export interface BotState extends RngHolder {
  side: Side;
  difficulty: BotDifficulty;
  plan: GamePlan | null;
  decisionT: number;
  mx: number;
  my: number;
  blockT: number;
  strafe: 1 | -1;
  /** Ring buffer of what the bot has seen of its opponent, newest last. */
  seen: Observation[];
  lastReactedSeq: number;
  /** Button to press on the next tick (moves act on a press, so it is released after one tick). */
  press: number;
  /** Punish window: 0 not decided yet, 1 going for the punish, −1 letting it pass. */
  punish: 0 | 1 | -1;
}

export function createBot(opts: {
  side: Side;
  difficulty: BotDifficulty;
  seed: number;
  plan?: GamePlan | null;
}): BotState {
  return {
    side: opts.side,
    difficulty: opts.difficulty,
    plan: opts.plan ?? null,
    rng: opts.seed | 0,
    decisionT: 0.5,
    mx: 0,
    my: 0,
    blockT: 0,
    strafe: 1,
    seen: [],
    lastReactedSeq: 0,
    press: 0,
    punish: 0,
  };
}

interface Tuning {
  approach: number;
  strike: number;
  grapple: number;
  block: number;
  signature: number;
  lowStamina: number;
  retreatUntil: number;
  signatureAsap: boolean;
}

function tuning(bot: BotState, s: FightState): Tuning {
  const b = BOT.base;
  const t: Tuning = {
    approach: 1,
    strike: b.strike,
    grapple: b.grapple,
    block: b.block,
    signature: b.signatureWhenFull,
    lowStamina: b.lowStamina,
    retreatUntil: b.lowStamina,
    signatureAsap: false,
  };
  const plan = bot.plan;
  if (!plan) return t;
  const style = BOT.plan.styles[plan.style];
  const roundPlan = BOT.plan.roundPlans[plan.rounds[s.round - 1] ?? 'push'];
  let aggression = plan.aggression + roundPlan.aggression;
  const me = s.fighters[bot.side];
  const hurt = me.hp / me.maxHp < BOT.plan.whenHurt.threshold;
  if (hurt && plan.whenHurt === 'swingBack') aggression += BOT.plan.whenHurt.swingBackAggression;
  const aggro = 0.5 + Math.max(0, Math.min(100, aggression)) / 100; // 0.5 … 1.5
  t.approach = style.approach * aggro;
  t.strike = b.strike * style.strike * aggro;
  t.grapple = b.grapple * style.grapple * aggro;
  t.block = (b.block * style.block * roundPlan.block) / aggro;
  t.lowStamina = b.lowStamina / aggro;
  t.retreatUntil = t.lowStamina;
  if (hurt && plan.whenHurt === 'cover') {
    t.block *= 2;
    t.retreatUntil = BOT.plan.whenHurt.coverStaminaTarget;
  }
  t.signatureAsap = plan.rounds[s.round - 1] === 'finish';
  return t;
}

/** Produces the bot's input for the next tick. Call once per tick, before step(). */
export function botInput(bot: BotState, s: FightState): FighterInput {
  const cfg = BOT.difficulties[bot.difficulty];
  const me = s.fighters[bot.side];
  const opp = s.fighters[bot.side === 0 ? 1 : 0];

  // Perception with a reaction delay.
  bot.seen.push({
    x: opp.x,
    y: opp.y,
    actType: opp.act?.type ?? null,
    actSeq: opp.actSeq,
    blocking: opp.blocking,
    rocked: opp.rocked > 0,
  });
  const delayTicks = Math.max(1, Math.round(cfg.reactionSeconds / TICK_SECONDS));
  if (bot.seen.length > delayTicks + 1) bot.seen.shift();
  const view = bot.seen[0]!;

  const press = bot.press;
  bot.press = 0;
  if (s.phase !== 'fight' || me.ko) {
    bot.blockT = 0;
    return { mx: 0, my: 0, buttons: 0 };
  }

  const t = tuning(bot, s);
  // Its reach against this opponent: Footwork makes strikes fall short (D49).
  const reach = reachOf(s.setup.fighters[bot.side].mods) * s.setup.fighters[bot.side === 0 ? 1 : 0].mods.evadeMul;
  const dx = view.x - me.x;
  const dy = view.y - me.y;
  const d = length(dx, dy) || 1;
  const ux = dx / d;
  const uy = dy / d;
  bot.blockT -= TICK_SECONDS;
  // Rocked (D13): the sim refuses attacks, so the bot only moves and blocks.
  const dazed = me.rocked > 0;

  // React to a move it has just noticed (after its reaction delay).
  if (view.actSeq !== bot.lastReactedSeq && view.actType) {
    bot.lastReactedSeq = view.actSeq;
    const incoming = view.actType === 'strike' || view.actType === 'strike2' || view.actType === 'signature';
    const blockMul = dazed ? BOT.base.rockedBlockMul : t.block / BOT.base.block;
    if (incoming && d < reach + BOT.base.reactRangeExtra && nextRandom(bot) < cfg.blockChance * blockMul) {
      bot.blockT = BOT.base.reactiveBlockSeconds;
    } else if (!dazed && view.actType === 'grapple' && d < reach * 1.6 && nextRandom(bot) < cfg.readChance) {
      bot.press = BUTTON.strike; // a strike beats a grapple
    }
  }

  // A read opened its punish window (D14): once it has noticed, strike back or let it pass.
  if (me.punishWindow > 0) {
    if (bot.punish === 0 && FIGHT.read.window - me.punishWindow >= cfg.reactionSeconds) {
      bot.punish = nextRandom(bot) < cfg.punishChance ? 1 : -1;
    }
  } else {
    bot.punish = 0;
  }
  if (bot.punish === 1 && !dazed && !me.act) {
    if (d <= reach) {
      bot.press = BUTTON.strike;
      bot.blockT = 0;
      bot.punish = -1;
    } else {
      bot.mx = ux;
      bot.my = uy;
      bot.blockT = 0;
    }
  }

  bot.decisionT -= TICK_SECONDS;
  if (bot.decisionT <= 0) {
    bot.decisionT = randomRange(bot, cfg.decisionMin, cfg.decisionMax);
    if (nextRandom(bot) < BOT.base.strafeSwitchChance) bot.strafe = bot.strafe === 1 ? -1 : 1;
    let mx: number;
    let my: number;
    const tired = me.stamina < t.lowStamina;
    const fin = BOT.base.finish;
    if (dazed) {
      // Rocked: get away, and cover up when the opponent is close.
      mx = -ux * 0.8 - uy * bot.strafe * 0.5;
      my = -uy * 0.8 + ux * bot.strafe * 0.5;
      if (d < reach * BOT.base.rockedCoverRange && nextRandom(bot) < cfg.blockChance * BOT.base.rockedBlockMul) {
        bot.blockT = BOT.base.blockSeconds;
      }
    } else if (view.rocked) {
      // The opponent is rocked: go for the finish, deciding faster, even when low on stamina
      // (attacks only when affordable). A grapple beats a fighter who covers up.
      bot.decisionT = Math.min(bot.decisionT, cfg.decisionMin * BOT.base.finishDecisionMul);
      mx = ux;
      my = uy;
      if (d <= reach) {
        const r = nextRandom(bot);
        const mods = s.setup.fighters[bot.side].mods;
        const canStrike = me.stamina >= moveCost(mods, 'strike');
        const canGrapple = me.stamina >= moveCost(mods, 'grapple');
        if (me.meter >= FIGHT.meterMax && r < fin.signature) bot.press = BUTTON.signature;
        else if (view.blocking) {
          if (canGrapple && r < fin.grappleIfBlocking) bot.press = BUTTON.grapple;
        } else if (canStrike && r < fin.strike) bot.press = BUTTON.strike;
        else if (canGrapple && r < fin.strike + fin.grapple) bot.press = BUTTON.grapple;
      }
    } else if (tired || (t.retreatUntil > t.lowStamina && me.stamina < t.retreatUntil && me.hp / me.maxHp < BOT.plan.whenHurt.threshold)) {
      mx = -ux * 0.8 - uy * bot.strafe * 0.5;
      my = -uy * 0.8 + ux * bot.strafe * 0.5;
      if (me.hp / me.maxHp < BOT.plan.whenHurt.threshold && nextRandom(bot) < t.block) bot.blockT = BOT.base.blockSeconds;
    } else if (d > reach * BOT.base.approachRange) {
      const side = 0.35 / Math.max(0.5, t.approach);
      mx = ux - uy * bot.strafe * side;
      my = uy + ux * bot.strafe * side;
    } else {
      mx = -uy * bot.strafe * 0.6;
      my = ux * bot.strafe * 0.6;
      if (view.blocking && nextRandom(bot) < cfg.readChance) {
        bot.press = BUTTON.grapple; // a grapple beats a block
      } else {
        const r = nextRandom(bot);
        const full = me.meter >= FIGHT.meterMax;
        if (full && (t.signatureAsap || r < t.signature)) bot.press = BUTTON.signature;
        else if (r < t.strike) bot.press = BUTTON.strike;
        else if (r < t.strike + t.grapple) bot.press = BUTTON.grapple;
        else if (r < t.strike + t.grapple + t.block) bot.blockT = BOT.base.blockSeconds;
      }
    }
    const len = length(mx, my) || 1;
    bot.mx = mx / len;
    bot.my = my / len;
  }

  let buttons = dazed ? press & BUTTON.defend : press;
  let mx = bot.mx;
  let my = bot.my;
  if (bot.blockT > 0) {
    buttons |= BUTTON.defend;
    mx *= 0.3;
    my *= 0.3;
  }
  return { mx, my, buttons };
}
